<script setup>
const props = defineProps({
  label: String,
  id: String,
  required: Boolean,
  multiple: Boolean,
  information: [String, null],
  files: { type: Array, default: [] },
});

const emit = defineEmits(["change", "remove"]);

const fieldDisabled = computed(() => {
  if (props.files && props.files.length > 0 && !props.multiple) return true;
});

const handleChange = (e) => {
  emit("change", e);
};
const handleRemove = (e, id, fileName, size) => {
  e.preventDefault();
  e.target.blur();
  e.target.parentNode.focus();
  emit("remove", { e, id, fileName, size });
};
</script>

<template>
  <div class="file-upload">
    <label class="file-upload__heading" :for="id">
      <ui-font-text type="s-bold">
        {{ label }} <span v-if="required">*</span></ui-font-text
      ></label
    >
    <div
      class="file-upload__container"
      :class="{ '-upload-disabled': fieldDisabled }"
    >
      <label class="file-upload__label" :for="id">
        <ui-font-text type="s">
          Drag or choose file<b v-if="multiple">s</b><ui-icons-upload
        /></ui-font-text>

        <div :class="['file-upload__input', { required }]">
          <input
            type="file"
            :id="id"
            :name="id"
            :required="required"
            :multiple="multiple"
            :disabled="fieldDisabled"
            @change="handleChange"
          />
        </div>
      </label>
    </div>
    <div class="file-upload__filenames" v-if="files && files.length">
      <div
        v-for="(file, idx) in files"
        :key="'filename' + idx"
        class="file-upload__filename"
        :title="file.fileName"
      >
        <div>{{ file.fileName }}</div>
        <button
          class="file-upload__remove"
          @click="handleRemove($event, id, file.fileName, file.size)"
        >
          <ui-icons-close />
        </button>
      </div>
    </div>
    <div class="file-upload__desc ts-trumpet o-5">
      <ui-font-text type="s"><span v-html="information"></span></ui-font-text>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.file-upload {
  --active-color: var(--current-theme);
  --inactive-color: hsla(0, 0%, 100%, 0.5);
  margin-top: 30px;

  b {
    font-weight: normal;
  }

  &__container {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 10px;
    padding: 10px;
    transition: border 0.25s ease;
    border: 1px solid var(--inactive-color);

    &:not(.-upload-disabled):hover {
      border: 1px solid var(--active-color);
    }

    &:focus-within {
      border: 1px solid var(--active-color);
    }

    &.-upload-disabled {
      cursor: default;
      opacity: 0.5;

      label {
        cursor: default;
      }
    }
  }

  &__desc {
    margin-top: 10px;
  }

  &__label {
    display: block;
    cursor: pointer;

    &:after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    p {
      display: flex;
      justify-content: center;
      align-items: center;

      svg {
        display: flex;
        justify-content: center;
        margin-left: 10px;

        height: 1em;
        width: auto;

        path {
          display: flex;
        }
      }
    }
  }

  &__filenames {
    display: block;
  }

  &__filename {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    border: 1px solid var(--inactive-color);
    margin-top: -1px;

    div {
      display: block;
      flex: 1;
      width: 150px;
      text-overflow: ellipsis;
      font-size: 90%;
      white-space: nowrap;
      overflow: hidden;
    }
  }

  &__input {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;
    opacity: 0;
    width: 0;
    height: 0;
  }

  &__remove {
    display: flex;
    place-items: center;
    width: 20px;
    height: 20px;
    padding: 4px;
    border-radius: 2px;
    border: 1px solid transparent;
    text-align: center;

    &:hover,
    &:focus {
      outline: 0;
      border: 1px solid var(--inactive-color);
    }

    svg {
      width: 12px;
    }
  }
}
</style>
